import { type CollectionVariantRef, variantSelectionKey } from '@ikegps/core-api';

export interface VariantRow<S> {
	/** The variant's type code (e.g. 'proposed'); empty string for the Existing (parent) row. */
	variantType: string;
	/** Human-readable variant type name (e.g. 'Proposed') supplied by the publisher, when present. */
	variantTypeName?: string;
	summary: S;
	/** True for the parent's own "Existing" row, false for variant children. */
	isExisting: boolean;
}

/** A collection is a variant container when it carries one or more variants. */
export function hasCollectionVariants(summary: { variants?: CollectionVariantRef[] }): boolean {
	return summary.variants != null && summary.variants.length > 0;
}

/**
 * Distinct `(variantType, variantTypeName)` pairs present across a set of parent collections'
 * `variants` arrays, in first-seen order. Two variants sharing a `variantType` but differing in
 * `variantTypeName` are distinct entries — deduping by `variantType` alone would silently drop
 * one of them. Does not include the synthetic "Existing" (parent) entry — callers add that
 * themselves, since it's always present regardless of what variants exist.
 */
export function distinctVariantTypes<S extends { variants?: CollectionVariantRef[] }>(
	parents: S[]
): { variantType: string; variantTypeName?: string }[] {
	const seen = new Map<string, { variantType: string; variantTypeName?: string }>();
	for (const parent of parents) {
		for (const ref of parent.variants ?? []) {
			const key = variantSelectionKey(ref.variantType, ref.variantTypeName);
			if (!seen.has(key)) {
				seen.set(key, { variantType: ref.variantType, variantTypeName: ref.variantTypeName });
			}
		}
	}
	return Array.from(seen.values());
}

/**
 * Ordered rows for a parent collection's variant container: the parent itself
 * ("Existing") first, then one row per variant in declared order. Variants whose
 * summary cannot be resolved yet (still loading) are omitted.
 */
export function buildVariantRows<S extends { variants?: CollectionVariantRef[] }>(
	parent: S,
	resolve: (uuid: string) => S | null
): VariantRow<S>[] {
	const rows: VariantRow<S>[] = [{ variantType: '', summary: parent, isExisting: true }];

	for (const ref of parent.variants ?? []) {
		const summary = resolve(ref.variantUuid);
		if (summary != null) {
			rows.push({
				variantType: ref.variantType,
				variantTypeName: ref.variantTypeName,
				summary,
				isExisting: false,
			});
		}
	}

	return rows;
}
